import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import esES from 'antd/locale/es_ES';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { antdTheme } from './styles/theme';
import './App.css';
import './styles/custom.css';
import './styles/mobile-cards.css';
import './styles/dashboard.css';

// Importar páginas existentes
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Articles from './pages/Articles';
import ArticleEditor from './pages/ArticleEditor';
import MiApp from './pages/MiApp';
import Trash from './pages/Trash';
import Users from './pages/Users';
import Profile from './pages/Profile';
import Calendar from './pages/Calendar';
import Mottos from './pages/Mottos';
import Notifications from './pages/Notifications';
import CaminoDashboard from './pages/Camino/CaminoDashboard';
import EtapaEditor from './pages/Camino/EtapaEditor';
import GaleriaEditor from './pages/Camino/GaleriaEditor';
import MochilaEditor from './pages/Camino/MochilaEditor';
import BrujulaEditor from './pages/Camino/BrujulaEditor';

// Importar nuevo layout
import AppLayout from './components/AppLayout';

// Rutas protegidas. allowCamino=true permite acceder al rol "camino";
// por defecto, los usuarios de rol camino se redirigen a /camino.
const ProtectedRoute = ({ children, allowCamino = false }) => {
  const { isAuthenticated, loading, isCamino } = useAuth();

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (isCamino() && !allowCamino) {
    return <Navigate to="/camino" replace />;
  }

  return children;
};

// Componente para rutas solo de admin
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin()) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Rutas de la sección Camino: accesibles para admin y para rol camino.
const CaminoRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin, isCamino } = useAuth();

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  if (!isAdmin() && !isCamino()) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Redirige la raíz "/" según el rol del usuario.
const RootRedirect = () => {
  const { isAuthenticated, loading, isCamino } = useAuth();
  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando...</div>;
  }
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to={isCamino() ? '/camino' : '/dashboard'} replace />;
};

// Componente para rutas públicas (solo para no autenticados)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, isCamino } = useAuth();

  if (loading) {
    return <div style={{ padding: '50px', textAlign: 'center' }}>Cargando...</div>;
  }

  if (isAuthenticated) {
    return <Navigate to={isCamino() ? '/camino' : '/dashboard'} />;
  }

  return children;
};

function AppContent() {
  return (
    <Router>
      <AppLayout>
        <div className="App">
          <Routes>
            {/* Ruta de login */}
            <Route 
              path="/login" 
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } 
            />
            
            {/* Rutas protegidas existentes */}
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/articles" 
              element={
                <ProtectedRoute>
                  <Articles />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/articles/new" 
              element={
                <ProtectedRoute>
                  <ArticleEditor />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/articles/edit/:id" 
              element={
                <ProtectedRoute>
                  <ArticleEditor />
                </ProtectedRoute>
              } 
            />
            
            {/* Nuevas rutas */}
            <Route 
              path="/mi-app" 
              element={
                <ProtectedRoute>
                  <MiApp />
                </ProtectedRoute>
              } 
            />
            
            <Route 
              path="/trash" 
              element={
                <ProtectedRoute>
                  <Trash />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/users" 
              element={
                <AdminRoute>
                  <Users />
                </AdminRoute>
              } 
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <Calendar />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mottos"
              element={
                <ProtectedRoute>
                  <Mottos />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              } 
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute allowCamino>
                  <Profile />
                </ProtectedRoute>
              }
            />

            {/* Camino de Santiago: admin + rol camino */}
            <Route
              path="/camino"
              element={
                <CaminoRoute>
                  <CaminoDashboard />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/etapa/nueva"
              element={
                <CaminoRoute>
                  <EtapaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/etapa/:id"
              element={
                <CaminoRoute>
                  <EtapaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/galeria/nueva"
              element={
                <CaminoRoute>
                  <GaleriaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/galeria/:id"
              element={
                <CaminoRoute>
                  <GaleriaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/mochila/nueva"
              element={
                <CaminoRoute>
                  <MochilaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/mochila/:id"
              element={
                <CaminoRoute>
                  <MochilaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/brujula/nueva"
              element={
                <CaminoRoute>
                  <BrujulaEditor />
                </CaminoRoute>
              }
            />
            <Route
              path="/camino/brujula/:id"
              element={
                <CaminoRoute>
                  <BrujulaEditor />
                </CaminoRoute>
              }
            />

            {/* Redirección por defecto */}
            <Route
              path="/"
              element={<RootRedirect />}
            />

            {/* Catch-all para rutas no encontradas */}
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <Navigate to="/dashboard" replace />
                </ProtectedRoute>
              }
            />
          </Routes>
        </div>
      </AppLayout>
    </Router>
  );
}

function App() {
  return (
    <ConfigProvider locale={esES} theme={antdTheme}>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </ConfigProvider>
  );
}

export default App;